/**
 * The two poses that are not lying flat: a man face down over his own knees,
 * and a man on his feet.
 *
 * The sprawler is the interesting one. He is folded over his knees with his
 * face on the boards, so the line from his forehead to his backside climbs the
 * whole height of him inside half a metre - nothing about him can be placed
 * the way a sleeper's parts are, at a point and level with the floor. Every
 * piece is laid between the two joints it joins, because a chain of level
 * boxes cannot say "bent", and bent is the entire figure.
 *
 * Same bargain as `People.ts`: hand it the asset store and a spec, get a group.
 *
 * Rendering only. Nothing in this file may ever be imported by `src/sim`.
 */

import * as THREE from 'three';
import { ballGeometry, buildHead, limbGeometry, pickHairStyle } from './Figure';
import { createSign } from './Sign';
import type { SceneAssets } from './SceneAssets';
import {
  BOOTS,
  HAIR,
  LIMB_AXIS,
  SHIRTS,
  SKIN_TONES,
  SPRAWLER_SEED,
  STANDER_SEED,
  TROUSERS,
  addCap,
  addHeadphones,
  sleeperFigure,
} from './People';
import { mulberry32 } from '../utils/prng';
import type { SprawlerSpec, StanderSpec } from '../world/Level';

/**
 * One man face down on the floor, with the floor under his hips at the
 * group's origin and local +Z the way his head is pointed.
 *
 * The third pose in the building, and the first one that is a *pose* rather
 * than a body: he is folded over his own knees with his face on the boards,
 * so the line from his forehead to his backside climbs the whole height of
 * him inside half a metre. Nothing here can be placed the way a sleeper's
 * parts are, at a point and level with the floor - every piece of him is laid
 * between the two joints it joins, because a chain of level boxes cannot say
 * "bent", and bent is the entire figure. His arms are why the joints are
 * points in space rather than a side view: they come forward *and* in, so his
 * hands end up either side of his head instead of out past his ears.
 *
 * His hips are the highest part of him and they are bare, because his
 * trousers are where they ended up rather than where they belong. That is
 * what puts a third piece in each leg: thigh, the cloth caught above the
 * knee, and the shin still inside it. The shirt is on him and rucked towards
 * his shoulders, which is what a shirt does to a man in this position and is
 * the only reason the small of his back is showing.
 */
export function sprawlerFigure(assets: SceneAssets, spec: SprawlerSpec, index: number): THREE.Group {
  const rng = mulberry32(0x51eeb0 + (SPRAWLER_SEED + index) * 977);
  const group = new THREE.Group();

  const h = spec.height;
  const skinHex = SKIN_TONES[(rng() * SKIN_TONES.length) | 0];
  const skin = assets.figureMaterial(skinHex);
  const shirt = assets.figureMaterial(SHIRTS[(rng() * SHIRTS.length) | 0]);
  const trousers = assets.figureMaterial(TROUSERS[(rng() * TROUSERS.length) | 0]);
  const hairHex = HAIR[(rng() * HAIR.length) | 0];
  const hair = assets.figureMaterial(hairHex);
  const round = limbGeometry();

  /** A joint in his own frame: out to one side, up off the floor, along him. */
  type Joint = readonly [x: number, y: number, z: number];

  const from = new THREE.Vector3();
  const to = new THREE.Vector3();
  const along = new THREE.Vector3();

  /**
   * One piece of him, laid from joint to joint: `wide` across it, `thick`
   * through it. `limbGeometry` runs along +Z, so the piece is turned by the
   * shortest rotation that takes +Z to the direction of travel - which for
   * anything in his own midline is a tilt about X and nothing else.
   */
  const bone = (material: THREE.Material, wide: number, thick: number, a: Joint, b: Joint): void => {
    from.set(a[0], a[1], a[2]);
    to.set(b[0], b[1], b[2]);
    const length = from.distanceTo(to);
    const mesh = new THREE.Mesh(round, material);
    mesh.scale.set(wide, thick, length);
    mesh.position.addVectors(from, to).multiplyScalar(0.5);
    mesh.quaternion.setFromUnitVectors(LIMB_AXIS, along.subVectors(to, from).divideScalar(length));
    group.add(mesh);
  };

  /** A block: a foot, a hand, the things that are flat rather than round. */
  const block = (material: THREE.Material, size: Joint, at: Joint): void => {
    const mesh = new THREE.Mesh(assets.boxGeometry, material);
    mesh.scale.set(size[0], size[1], size[2]);
    mesh.position.set(at[0], at[1], at[2]);
    group.add(mesh);
  };

  // The spine: shoulders on the boards, hips in the air, and the taper
  // between them doing the same job it does on every other body here.
  const shoulder: Joint = [0, h * 0.1, h * 0.34];
  const chest: Joint = [0, h * 0.15, h * 0.23];
  const waist: Joint = [0, h * 0.2, h * 0.1];
  const hip: Joint = [0, h * 0.245, 0];
  bone(shirt, h * 0.25, h * 0.155, shoulder, chest);
  bone(shirt, h * 0.225, h * 0.15, chest, waist);
  bone(skin, h * 0.2, h * 0.145, waist, hip);
  // The hem, hanging off him towards his head rather than lying against him.
  bone(shirt, h * 0.235, h * 0.165, [0, h * 0.17, h * 0.19], [0, h * 0.195, h * 0.09]);

  // The top of him, and the highest thing in the room that is not furniture.
  // Two of them and set a little apart, because one ball is a hump.
  for (const side of [-1, 1]) {
    const cheek = new THREE.Mesh(ballGeometry(), skin);
    cheek.scale.set(h * 0.108, h * 0.125, h * 0.135);
    cheek.position.set(side * h * 0.062, h * 0.295, -h * 0.055);
    group.add(cheek);
  }

  for (const side of [-1, 1]) {
    const x = side * h * 0.062;
    const knee: Joint = [x, h * 0.085, -h * 0.19];
    const ankle: Joint = [x, h * 0.045, -h * 0.43];
    // Knees tucked under him, which is what holds the rest of him up there.
    bone(skin, h * 0.105, h * 0.105, [x, h * 0.235, -h * 0.02], knee);
    bone(trousers, h * 0.095, h * 0.095, knee, ankle);
    // And the cloth: gathered over the knee, which is as far as it got. The
    // thigh above it is the reason the rest of this figure is worth drawing.
    bone(trousers, h * 0.145, h * 0.145, [x, h * 0.115, -h * 0.14], [x, h * 0.07, -h * 0.26]);
    // Shins flat on the boards, so the soles are what faces the ceiling.
    block(skin, [h * 0.055, h * 0.055, h * 0.1], [x, h * 0.035, -h * 0.475]);
  }

  // Arms forward and folded in, so his hands finish either side of his head.
  // Nobody places his hands on the way down; they end up where they land, and
  // one elbow is always further out than the other.
  for (const side of [-1, 1]) {
    const out = side * h * (0.155 + rng() * 0.03);
    bone(shirt, h * 0.075, h * 0.075, [side * h * 0.125, h * 0.105, h * 0.33], [out, h * 0.06, h * 0.45]);
    bone(skin, h * 0.065, h * 0.065, [out, h * 0.06, h * 0.45], [side * h * 0.085, h * 0.035, h * 0.55]);
    block(skin, [h * 0.05, h * 0.035, h * 0.08], [side * h * 0.075, h * 0.024, h * 0.6]);
  }

  // The neck, which on a man lying like this is the only thing not sloping.
  bone(skin, h * 0.075, h * 0.08, shoulder, [0, h * 0.085, h * 0.44]);

  const radius = h * 0.065;
  const head = buildHead({
    radius,
    skin: skinHex,
    hair: hairHex,
    style: pickHairStyle(rng),
    expression: 'asleep',
    skinMaterial: skin,
    hairMaterial: hair,
    rng,
    stubble: rng() < 0.6,
    moustache: rng() < 0.15,
  });
  // Built upright and facing +Z, so: tipped face-first onto the boards (x),
  // then rolled onto one cheek (z), because nobody stays on his nose. Same
  // reading as a sleeper's head, and the same innermost-first order.
  head.rotation.set(Math.PI / 2, 0, (rng() < 0.5 ? -1 : 1) * (0.7 + rng() * 0.3), 'ZYX');
  head.position.set(0, radius * 0.9, h * 0.51);
  group.add(head);

  return group;
}

/**
 * The other way of being on a floor: flat out along the length of himself,
 * dressed, and asleep. See `SprawlerSpec.pose`.
 *
 * Drawn by `sleeperFigure` rather than by a second pose written out here,
 * because that is exactly what this is - a sleeping body with no bed under
 * it. Everything that figure derives, it derives from the man rather than
 * from the bed, so all this has to supply is how big he is: as long as he is
 * tall, about half that across with his arms beside him, and thick enough to
 * be a man on the grass rather than a groundsheet somebody dropped.
 *
 * The seed is the one `sprawlerFigure` would have used at the same index,
 * and both figures roll skin, shirt, trousers and hair in that order - so
 * rolling a man from one pose into the other leaves him in his own clothes.
 */
export function flatSprawlerFigure(assets: SceneAssets, spec: SprawlerSpec, index: number): THREE.Group {
  const h = spec.height;
  const body = sleeperFigure(assets, 
    {
      name: spec.name,
      center: [0, 0],
      orientation: 'z',
      length: h,
      width: h * 0.45,
      height: h * 0.22,
    },
    SPRAWLER_SEED + index,
  );

  // A sleeper is laid out about its own middle and carries its hips a little
  // behind that; a sprawler is placed *by* its hips. So the body slides up
  // by the difference and the two conventions stop disagreeing - which is
  // the whole reason this is a group and not a figure returned as it came.
  body.position.z = h * 0.06;

  const group = new THREE.Group();
  group.add(body);
  return group;
}

/**
 * One man on his feet, with the ground under him at the group's origin and
 * local +Z the way he is looking. See `StanderSpec`.
 *
 * The fourth pose in the venue and the plainest of the four: a sleeper is a
 * body laid on a box, a sitter is one folded over a bench and a sprawler is
 * one folded over itself, and this one is just standing there. What it costs
 * to draw is therefore not the trunk - it is the arms, because a man stood
 * upright with his arms hanging is a man queueing, and the only person in the
 * yard standing up is standing up to *do* something.
 *
 * So the arms are hung off two groups, `kasi-vasen` and `kasi-oikea`, pivoted
 * at the shoulder with the elbow bent inside them and nothing else posed.
 * Whatever he is doing sets those two angles and reaches for the hands, the
 * same split `buildHannuFigure` uses for the arm that swings.
 */
export function standerFigure(assets: SceneAssets, spec: StanderSpec, index: number): THREE.Group {
  const rng = mulberry32(0x51eeb0 + (STANDER_SEED + index) * 977);
  const group = new THREE.Group();

  const h = spec.height;
  /** The across-the-body scale, read the same way a sitter's is. */
  const shoulder = h * 0.26;

  const skinHex = SKIN_TONES[(rng() * SKIN_TONES.length) | 0];
  const skin = assets.figureMaterial(skinHex);
  // A printed shirt wears the colour its print is backed with: a word on a
  // patch of some other colour is a sign safety-pinned to a man, not a
  // t-shirt. The roll happens either way, so dressing one figure in a
  // printed shirt cannot change what the next one looks like.
  const print = spec.shirt;
  const rolledShirt = SHIRTS[(rng() * SHIRTS.length) | 0];
  const shirt = assets.figureMaterial(
    print ? new THREE.Color(print.background).getHex() : rolledShirt,
  );
  const trousers = assets.figureMaterial(TROUSERS[(rng() * TROUSERS.length) | 0]);
  const hairHex = HAIR[(rng() * HAIR.length) | 0];
  const hair = assets.figureMaterial(hairHex);

  /** One part: `across` him, `tall` off the floor, `deep` the way he faces. */
  const part = (
    parent: THREE.Object3D,
    material: THREE.Material,
    across: number,
    tall: number,
    deep: number,
    x: number,
    y: number,
    z: number,
  ): THREE.Mesh => {
    const mesh = new THREE.Mesh(assets.boxGeometry, material);
    mesh.scale.set(across, tall, deep);
    mesh.position.set(x, y, z);
    parent.add(mesh);
    return mesh;
  };

  // Hips, waist, chest: the same three-box taper every body here is built
  // from, stood on end and with the fractions a standing man's are.
  part(group, trousers, shoulder * 0.84, h * 0.1, h * 0.16, 0, h * 0.55, 0);
  part(group, shirt, shoulder * 0.8, h * 0.11, h * 0.145, 0, h * 0.66, 0);
  const chest = part(group, shirt, shoulder, h * 0.14, h * 0.165, 0, h * 0.775, 0);

  // The word across his chest, drawn by the thing that draws every other word
  // in the venue and standing a millimetre proud of the cloth so it cannot
  // z-fight with it. A shirt is a cap you wear lower down: at a LAN both are
  // there to be read by somebody who has never met you.
  if (print) {
    group.add(
      createSign({
        text: print.text,
        position: { x: 0, y: chest.position.y, z: chest.scale.z / 2 + 0.001 },
        rotationY: 0,
        width: shoulder * 0.66,
        height: h * 0.062,
        color: print.color,
        background: print.background,
      }),
    );
  }

  for (const side of [-1, 1]) {
    const x = side * shoulder * 0.25;
    // Thigh, shin, and a boot on the end of it: he is the one man in the
    // level who is outdoors on purpose, and everybody else is barefoot
    // because everybody else is in a building.
    part(group, trousers, shoulder * 0.32, h * 0.25, h * 0.17, x, h * 0.4, 0);
    part(group, trousers, shoulder * 0.28, h * 0.25, h * 0.13, x, h * 0.16, 0);
    part(group, assets.figureMaterial(BOOTS), shoulder * 0.3, h * 0.04, h * 0.15, x, h * 0.02, h * 0.03);
  }

  // The neck, narrower than the head over it, exactly as the sitter's is.
  part(group, skin, shoulder * 0.22, h * 0.06, h * 0.075, 0, h * 0.855, 0);

  // Arms: upper arm hanging from the shoulder group, forearm bent at the
  // elbow inside it, hand on the end. Left at rest, and it is the caller
  // who lifts either of them - see the note above.
  for (const side of [-1, 1]) {
    const arm = new THREE.Group();
    arm.name = side > 0 ? 'kasi-vasen' : 'kasi-oikea';
    arm.position.set(side * shoulder * 0.52, h * 0.8, 0);
    group.add(arm);
    part(arm, shirt, shoulder * 0.24, h * 0.2, h * 0.12, 0, -h * 0.1, 0);

    const elbow = new THREE.Group();
    elbow.name = 'kyynarpaa';
    elbow.position.y = -h * 0.2;
    arm.add(elbow);
    part(elbow, shirt, shoulder * 0.22, h * 0.17, h * 0.11, 0, -h * 0.085, 0);
    // Named, because whatever he is holding is hung off it by whoever posed
    // the arm - see `addCook` and the lasta in `addBreakfast`.
    part(elbow, skin, shoulder * 0.2, h * 0.05, h * 0.1, 0, -h * 0.195, h * 0.01).name = 'kammen';
  }

  // Head in a group of its own, so a man looking off to one side takes his
  // cap and his lettering with him. Same as the sitter, and for the same
  // reason: the lettering is the only thing that says who he is.
  const radius = h * 0.065;
  const head = new THREE.Group();
  head.name = 'paa';
  head.position.set(0, h * 0.935, 0);
  group.add(head);

  const cap = spec.cap;
  head.add(
    buildHead({
      radius,
      skin: skinHex,
      hair: hairHex,
      style: cap ? 'capped' : pickHairStyle(rng),
      expression: 'delighted',
      blackoutFace: spec.name === 'dj-solidi' ? 'sad' : spec.cheering,
      skinMaterial: skin,
      hairMaterial: hair,
      rng,
      stubble: rng() < 0.6,
      moustache: rng() < 0.3,
    }),
  );
  if (cap) addCap(assets, head, cap, radius, h);
  // And the other thing a man can have on his head here. Nobody wears both:
  // see `StanderSpec.headphones`.
  if (spec.headphones) addHeadphones(assets, head, radius);

  return group;
}
